import {
	createFileRoute,
	stripSearchParams,
	useNavigate,
	useRouteContext,
	useSearch,
} from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { cursorToOffset, offsetToCursor } from "graphql-relay";
import { useEffect, useTransition } from "react";
import { graphql, useLazyLoadQuery, useRefetchableFragment } from "react-relay";
import z from "zod";
import { DisabledScreenAlert } from "@/components/disabled-screen-alert";
import { EventsTable } from "@/components/tables/events";
import type { eventosPageFragment$key } from "./__generated__/eventosPageFragment.graphql";
import type { eventosQuery } from "./__generated__/eventosQuery.graphql";
import type { eventosTableQuery } from "./__generated__/eventosTableQuery.graphql";

const defaultValues = {
	page: 1,
} as const;

export const Route = createFileRoute(
	"/_logged/c/$club/(sidebar)/(admins)/area-de-membros/eventos/",
)({
	component: RouteComponent,
	validateSearch: zodValidator(
		z.object({
			page: z.number().min(1).default(1),
		}),
	),
	search: {
		middlewares: [stripSearchParams(defaultValues)],
	},
});

function RouteComponent() {
	const { club } = useRouteContext({ from: "/_logged/c/$club" });
	const { page } = useSearch({ from: Route.id });
	const [isPending, startTransition] = useTransition();
	const navigate = useNavigate({
		from: Route.fullPath,
	});
	const data = useLazyLoadQuery<eventosQuery>(
		graphql`
			query eventosQuery($slug: String!, $first: Int, $last: Int, $after: String, $before: String) {
				club(slug: $slug) {
					id
					eventsEnabled: configuration(key: "EVENTS_ENABLED")
					...eventosPageFragment @arguments(first: $first, last: $last, after: $after, before: $before)
				}
			}
		`,
		{
			slug: club?.slug,
			first: 15,
			after: page === 1 ? undefined : offsetToCursor((page - 1) * 15 - 1),
		},
	);

	const [events, refetch] = useRefetchableFragment<
		eventosTableQuery,
		eventosPageFragment$key
	>(
		graphql`
			fragment eventosPageFragment on Club
				@refetchable(queryName: "eventosTableQuery")
				@argumentDefinitions(
					first: { type: "Int" }
					last: { type: "Int" }
					after: { type: "String" }
					before: { type: "String" }
				) {
				events(first: $first, after: $after, before: $before, last: $last) {
					edges {
						node {
							id
							slug
							title
							startDate
							duration
							attendeeCount
						}
						cursor
					}
					pageInfo {
						hasNextPage
						hasPreviousPage
						totalCount
						startCursor
						endCursor
					}
				}
			}
		`,
		data.club,
	);

	useEffect(() => {
		startTransition(() => {
			refetch(
				{
					first: 15,
					after: page === 1 ? undefined : offsetToCursor((page - 1) * 15 - 1),
				},
				{ fetchPolicy: "store-or-network" },
			);
		});
	}, [page, refetch]);

	const isDisabled = data.club.eventsEnabled === "0";
	const refreshEvents = () => {
		startTransition(() => {
			refetch({}, { fetchPolicy: "network-only" });
		});
	};

	return (
		<div className="p-6 flex flex-col gap-6">
			{isDisabled && (
				<DisabledScreenAlert
					clubId={data.club.id}
					configKey="EVENTS_ENABLED"
					featureName="Eventos"
				/>
			)}
			<EventsTable
				data={
					events?.events?.edges
						.filter((e) => Boolean(e.node))
						.map(({ node }) => node) || []
				}
				isPending={isPending}
				onDeleted={refreshEvents}
				totalCount={events?.events.pageInfo.totalCount || 0}
				hasNextPage={Boolean(events?.events.pageInfo.hasNextPage)}
				hasPreviousPage={Boolean(events?.events.pageInfo.hasPreviousPage)}
				loadNext={() =>
					startTransition(() => {
						navigate({ search: (s) => ({ ...s, page: s.page + 1 }) });
					})
				}
				loadPrevious={() =>
					startTransition(() => {
						navigate({ search: (s) => ({ ...s, page: s.page - 1 }) });
					})
				}
				startIndex={
					events?.events.edges[0]
						? cursorToOffset(events?.events.edges[0].cursor) + 1
						: 1
				}
				endIndex={
					events?.events.edges[events?.events.edges.length - 1]
						? cursorToOffset(
								events?.events.edges[events?.events.edges.length - 1].cursor,
							) + 1
						: 1
				}
			/>
		</div>
	);
}
